/**
 * uiState
 *
 * Base UI state singleton: ephemeral per-user chrome no Firestore document owns.
 * Each resizable panel is its own nested leaf, so a drag on one cannot re-render a
 * consumer reading another — the hook this replaced kept a shared hover flag that
 * re-rendered every panel. Widths load from `localStorage` at module load and
 * persist on drag end, so no effect and no React state sits in the sizing path.
 * A stored width is clamped on write and re-clamped on read, because the bounds
 * move under it: the viewport narrows, and the mapping review panel's cap follows
 * the live agent width. Every consumer reads with `{ sync: true }`: a drag must
 * not trail the pointer by a microtask, and one pointer event is one mutation
 * either way, so unbatching costs no extra render.
 * `signifyFetchJob` is the one leaf that sizes nothing: it points at the multi-part fetch each table
 * is following, keyed by that table's `targetKey`, and a reload reattaches by querying the operator's
 * running jobs, so it is a pointer and never the truth. One key per table is what lets several tables
 * on a form fetch at once, which the server has always allowed; readers take the leaf
 * (`snap[targetKey]`) so a write under another table's key re-renders nothing here.
 * `PanelKey` is therefore the width-bearing keys rather than `keyof UIState`.
 * Below antd `md` a panel's bounds span the viewport, unless it is marked `belowMd: 'own'`
 * because its component lays itself out there.
 */

import { theme } from 'antd';
import { proxy } from 'valtio';

type PanelWidthState = { width: number };

type AgentSidebarState = PanelWidthState & {
  isOpen: boolean;
  /** Viewport side the agent panel docks to; also the drag sign of its handle. */
  position: 'left' | 'right';
};

type UIState = {
  agentSidebar: AgentSidebarState;
  mappingReviewPanel: PanelWidthState;
  recordListPane: PanelWidthState;
  formSidebar: PanelWidthState;
  compositeFieldSidebar: PanelWidthState;
  signifyConfigurationSidebar: PanelWidthState;
  workflowParameterSidebar: PanelWidthState;
  workflowParametersEditor: PanelWidthState;
  formDerivesPanel: PanelWidthState;
  formDerivedValuesDrawer: PanelWidthState;
  arTableEditor: PanelWidthState;
  arColumnEditor: PanelWidthState;
  arFormulaSequenceEditor: PanelWidthState;
  layoutColumnConfig: PanelWidthState;
  signifyFetchJob: SignifyFetchJobUIState;
};

type SignifyFetchJobUIState = {
  /** Job each table's Signify fetch modal is following, by `targetKey`; no key shows its entry step. */
  activeJobIdByTable: Record<string, string>;
};

type PanelKey = {
  [K in keyof UIState]: UIState[K] extends PanelWidthState ? K : never;
}[keyof UIState];

type PanelBounds = {
  /** Stable per-panel identifier, namespaced into the localStorage key. */
  storageId: string;
  default: number;
  min: number;
  /** Absolute upper bound, before the viewport reserve is applied. */
  max?: number;
  /** Page kept visible beside the panel, so it can never cover a narrow window. */
  reserve?: number | (() => number);
  /** Pixels moved per arrow key press. */
  step: number;
  /**
   * Sizing below antd `md`. `fill` (default): the bounds span the viewport.
   * `own`: the desktop bounds hold, because the component lays itself out
   * below `md` (`Agent`, `MappingReviewPanel`).
   */
  belowMd?: 'fill' | 'own';
};

/** antd `md`: below it a filling panel's bounds span the viewport. */
const SCREEN_MD = theme.getDesignToken().screenMD;
const STEP = 20;
/** Page kept beside a settings/editor drawer. */
const DRAWER_RESERVE = 200;
/** Gutter between the mapping review panel and the chat it sits beside. */
const CHAT_GUTTER = 75;
/** Page kept beside the agent panel. */
const AGENT_MARGIN = 40;
/** Detail pane the records browser never lets the list swallow. */
const RECORD_DETAIL_MIN = 400;

const PANEL_BOUNDS: Record<PanelKey, PanelBounds> = {
  agentSidebar: {
    storageId: 'agent',
    default: 450,
    min: 320,
    reserve: AGENT_MARGIN,
    step: STEP,
    belowMd: 'own',
  },
  mappingReviewPanel: {
    storageId: 'mappingReview',
    default: 960,
    min: 560,
    reserve: () =>
      clampPanelWidth('agentSidebar', uiState.agentSidebar.width) + CHAT_GUTTER,
    step: STEP,
    belowMd: 'own',
  },
  recordListPane: {
    storageId: 'recordList',
    default: 380,
    min: 280,
    max: 640,
    reserve: RECORD_DETAIL_MIN,
    step: STEP,
  },
  formSidebar: {
    storageId: 'formSidebar',
    default: 500,
    min: 300,
    max: 1000,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  compositeFieldSidebar: {
    storageId: 'compositeFieldSidebar',
    default: 480,
    min: 300,
    max: 1000,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  signifyConfigurationSidebar: {
    storageId: 'signifyConfigurationSidebar',
    default: 736,
    min: 400,
    max: 1100,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  workflowParameterSidebar: {
    storageId: 'workflowParameterSidebar',
    default: 480,
    min: 320,
    max: 900,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  workflowParametersEditor: {
    storageId: 'workflowParameters',
    default: 736,
    min: 360,
    max: 1000,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  formDerivesPanel: {
    storageId: 'formDerives',
    default: 520,
    min: 360,
    max: 900,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  formDerivedValuesDrawer: {
    storageId: 'formDerivedValues',
    default: 560,
    min: 360,
    max: 900,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  arTableEditor: {
    storageId: 'arTableEditor',
    default: 720,
    min: 480,
    max: 1200,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  arColumnEditor: {
    storageId: 'arColumnEditor',
    default: 480,
    min: 360,
    max: 900,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  arFormulaSequenceEditor: {
    storageId: 'arFormulaSequence',
    default: 520,
    min: 360,
    max: 900,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
  layoutColumnConfig: {
    storageId: 'layoutColumnConfig',
    default: 560,
    min: 360,
    max: 1000,
    reserve: DRAWER_RESERVE,
    step: STEP,
  },
};

const storageKey = (panel: PanelKey): string =>
  `panelWidth-${PANEL_BOUNDS[panel].storageId}`;

const fillsViewport = (panel: PanelKey): boolean =>
  PANEL_BOUNDS[panel].belowMd !== 'own' && window.innerWidth < SCREEN_MD;

/**
 * Upper bound for a panel, against the live viewport. Read at event time and at
 * render time; never stored, so a window resize needs no listener. The panel's
 * own CSS `max-width` does the visual clamping between renders. Below antd `md`
 * a filling panel's bound is the viewport.
 */
export const panelMaxWidth = (panel: PanelKey): number => {
  if (fillsViewport(panel)) return window.innerWidth;
  const { min, max, reserve } = PANEL_BOUNDS[panel];
  const reserved = typeof reserve === 'function' ? reserve() : (reserve ?? 0);
  return Math.max(min, Math.min(max ?? Infinity, window.innerWidth - reserved));
};

/**
 * The same upper bound as `panelMaxWidth`, expressed for CSS so the browser
 * re-clamps on a window resize with no listener, no state and no render. The
 * `100vw` term is what survives between renders; a dynamic reserve is resolved to
 * its value now, and the ordinary re-render of whichever component reads that
 * reserve's source refreshes it. This stays an outer bound rather than the exact
 * one, because `100vw` counts a scrollbar the panel does not get.
 * A filling panel wraps that bound in a step at antd `md`:
 * `max(desktop, min(100%, 100vw, calc((768px - 100vw) * 10000)))`. At 768px and
 * wider the third term is <= 0, so `desktop` wins. Below 768px it is >= 10000px,
 * so `min` yields `min(100%, 100vw)`, and `max` yields that because `desktop` <
 * 100vw. `100vw` caps the step because on a phone whose layout viewport grew past
 * the device width, `100%` of the fixed Drawer root is that grown width.
 */
export const panelViewportClamp = (panel: PanelKey): string => {
  const { max, reserve, belowMd } = PANEL_BOUNDS[panel];
  const reserved = typeof reserve === 'function' ? reserve() : (reserve ?? 0);
  const viewport = `calc(100vw - ${reserved}px)`;
  const desktop = max === undefined ? viewport : `min(${max}px, ${viewport})`;
  return belowMd === 'own'
    ? desktop
    : `max(${desktop}, min(100%, 100vw, calc((${SCREEN_MD}px - 100vw) * 10000)))`;
};

/** Lower bound for a panel; below antd `md` a filling panel's floor never exceeds the viewport. */
export const panelMinWidth = (panel: PanelKey): number =>
  fillsViewport(panel)
    ? Math.min(PANEL_BOUNDS[panel].min, window.innerWidth)
    : PANEL_BOUNDS[panel].min;

export const panelStep = (panel: PanelKey): number => PANEL_BOUNDS[panel].step;

/**
 * Re-clamp a stored width against the live bounds. Consumers that compute layout
 * from a width must call this at read time: the stored number is only clamped on
 * write, so a viewport that has since narrowed — or, for the mapping review panel,
 * a chat that has since widened — would otherwise strand the panel off-screen.
 * The antd drawers do not need it; their `styles.wrapper` CSS clamp is the only
 * consumer of their width.
 */
export const clampPanelWidth = (panel: PanelKey, width: number): number =>
  Math.max(panelMinWidth(panel), Math.min(panelMaxWidth(panel), width));

/**
 * A stored width outside the panel's bounds is discarded for the default rather
 * than repaired, so a width saved on a wide monitor can never strand a panel
 * off-screen. The ceiling is the static `max` and the current viewport less the
 * static reserve, floored at `min` so a narrow window cannot reject every stored
 * width. A dynamic reserve counts as 0 here: this runs at module load, before
 * `uiState` is assigned, so its thunk cannot be called — read-time
 * `clampPanelWidth` applies the exact bound instead.
 */
const readStoredWidth = (panel: PanelKey): number => {
  const { default: defaultWidth, min, max, reserve } = PANEL_BOUNDS[panel];
  const staticReserve = typeof reserve === 'function' ? 0 : (reserve ?? 0);
  const ceiling = Math.max(
    min,
    Math.min(max ?? Infinity, window.innerWidth - staticReserve),
  );
  try {
    const stored = localStorage.getItem(storageKey(panel));
    if (stored === null) return defaultWidth;
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'number' || !Number.isFinite(parsed))
      return defaultWidth;
    if (parsed < min || parsed > ceiling) return defaultWidth;
    return parsed;
  } catch {
    return defaultWidth;
  }
};

/**
 * Persist a panel width. A `localStorage` that refuses the write — private mode,
 * quota — is swallowed: the width is cosmetic and the in-memory value still holds.
 */
const writeStoredWidth = (panel: PanelKey, width: number): void => {
  try {
    localStorage.setItem(storageKey(panel), JSON.stringify(width));
  } catch {
    return;
  }
};

export const uiState = proxy<UIState>({
  agentSidebar: {
    width: readStoredWidth('agentSidebar'),
    isOpen: false,
    position: 'right',
  },
  mappingReviewPanel: { width: readStoredWidth('mappingReviewPanel') },
  recordListPane: { width: readStoredWidth('recordListPane') },
  formSidebar: { width: readStoredWidth('formSidebar') },
  compositeFieldSidebar: { width: readStoredWidth('compositeFieldSidebar') },
  signifyConfigurationSidebar: {
    width: readStoredWidth('signifyConfigurationSidebar'),
  },
  workflowParameterSidebar: {
    width: readStoredWidth('workflowParameterSidebar'),
  },
  workflowParametersEditor: {
    width: readStoredWidth('workflowParametersEditor'),
  },
  formDerivesPanel: { width: readStoredWidth('formDerivesPanel') },
  formDerivedValuesDrawer: {
    width: readStoredWidth('formDerivedValuesDrawer'),
  },
  arTableEditor: { width: readStoredWidth('arTableEditor') },
  arColumnEditor: { width: readStoredWidth('arColumnEditor') },
  arFormulaSequenceEditor: {
    width: readStoredWidth('arFormulaSequenceEditor'),
  },
  layoutColumnConfig: { width: readStoredWidth('layoutColumnConfig') },
  signifyFetchJob: { activeJobIdByTable: {} },
});

/**
 * Clamp and store a panel width. `persist: false` during a drag keeps every
 * pointer-move tick out of `localStorage`; the pointer-up call writes it.
 */
export const setPanelWidth = (
  panel: PanelKey,
  next: number,
  { persist = true }: { persist?: boolean } = {},
): void => {
  const clamped = clampPanelWidth(panel, next);
  uiState[panel].width = clamped;
  if (persist) writeStoredWidth(panel, clamped);
};

export type { PanelKey, UIState };
